<template>
    <b-modal
        id="user-create-modal"
        ref="modal"
        :title="$t('새 사용자 생성')"
        modal-class="default-modal"
        @hidden="resetModal"
        @hide="resetModal"
    >
        <b-form id="user-create-form" @submit.prevent="submitForm">
            <b-form-group
                :label="$t('이메일 (로그인 ID)')"
                label-for="create-user-email"
            >
                <b-form-input
                    id="create-user-email"
                    v-model="form.e_mail"
                    type="email"
                    :placeholder="$t('이메일을 입력하세요')"
                    required
                    trim
                />
            </b-form-group>
            <b-form-group
                :label="$t('사용자명')"
                label-for="create-user-name"
            >
                <b-form-input
                    id="create-user-name"
                    v-model="form.user_name"
                    :placeholder="$t('사용자명을 입력하세요')"
                    required
                    trim
                />
            </b-form-group>
            <b-form-group
                :label="$t('비밀번호')"
                label-for="create-user-password"
                description="비우면 임시 비밀번호를 만들어 생성 후 한 번 보여줍니다."
            >
                <b-form-input
                    id="create-user-password"
                    v-model="form.password"
                    type="password"
                    :placeholder="$t('비밀번호 (선택)')"
                    trim
                />
            </b-form-group>
            <b-form-group
                :label="$t('사용자 레벨')"
                label-for="create-user-level"
            >
                <b-form-select
                    id="create-user-level"
                    v-model="form.is_admin"
                    :options="adminOptions"
                />
            </b-form-group>
        </b-form>
        <template #modal-footer>
            <b-button variant="outline-secondary" @click="$bvModal.hide('user-create-modal')">
                {{ $t('취소') }}
            </b-button>
            <b-button
                variant="primary"
                :disabled="!isValid || loading"
                form="user-create-form"
                type="submit"
            >
                <b-spinner v-if="loading" small class="mr-1" />
                {{ $t('생성') }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';

// 서버 오류 코드(평문 본문) -> 관리자용 문구 (admin.py insert_member)
const CREATE_ERROR_MESSAGES = {
    Invalid_Email_Format: '이메일 형식이 올바르지 않습니다.',
    Not_Found_Company: '존재하지 않는 기업입니다.',
    Not_Found_Request: '평가 신청된 기업이 아닙니다.',
    'Already exists Users': '이미 존재하는 이메일입니다.',
};

// 헷갈리는 글자(0/O, 1/l/I)를 뺀 임시 비밀번호
const TEMP_PASSWORD_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
const makeTempPassword = (length = 10) => Array.from(
    window.crypto.getRandomValues(new Uint32Array(length)),
    n => TEMP_PASSWORD_CHARS[n % TEMP_PASSWORD_CHARS.length]
).join('');

export default {
    name: 'UserCreateModal',
    props: {
        companyId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            form: {
                e_mail: '',
                user_name: '',
                password: '',
                is_admin: 1,
            },
            adminOptions: [
                { value: 1, text: '일반 사용자' },
                { value: 2, text: '외부 평가자' },
                { value: 3, text: '캠페인 평가자' },
            ],
        };
    },
    computed: {
        isValid() {
            return this.form.e_mail && this.form.user_name;
        },
    },
    methods: {
        async submitForm() {
            try {
                this.loading = true;
                const payload = {
                    company_id: Number(this.companyId),
                    e_mail: this.form.e_mail,
                    user_name: this.form.user_name,
                    is_admin: this.form.is_admin,
                };
                // 서버 자동 생성 비밀번호는 응답에 없어 아무도 알 수 없다 — 비워 두면 여기서 만들어 보내고 한 번 보여준다
                const tempPassword = this.form.password ? null : makeTempPassword();
                payload.password = this.form.password || tempPassword;

                const res = await this.$axios.post('/admin/company/member', payload);

                this.$bvModal.hide('user-create-modal');
                this.$emit('created', res.data);
                this.showCreatedModal(res.data && res.data.e_mail || payload.e_mail, tempPassword);
            } catch (e) {
                console.error(e);
                const status = e.response?.status;
                if (status === 401) return;
                const code = getErrorMessage(e, '');
                const message = CREATE_ERROR_MESSAGES[code]
                    || (status === 409 ? '이미 존재하는 이메일입니다.' : null)
                    || (code ? `사용자 생성 중 오류가 발생했습니다. (${code})` : '사용자 생성 중 오류가 발생했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('생성 실패'), message);
            } finally {
                this.loading = false;
            }
        },
        showCreatedModal(email, tempPassword) {
            if (!tempPassword) {
                this.$showModal({type: 'alert', state: 'success', title: '사용자 생성 완료', message: '사용자가 생성되었습니다.'});
                return;
            }
            const h = this.$createElement;
            this.$showModal({
                type: 'alert',
                state: 'success',
                title: '사용자 생성 완료',
                message: h('div', [
                    h('p', {class: 'mb-2 text-secondary'}, `${email} 계정을 만들었습니다.`),
                    h('div', {class: 'd-flex align-items-center mb-2'}, [
                        h('span', {class: 'mr-2'}, '초기 비밀번호:'),
                        h('code', {class: 'mr-2 h6 mb-0 user-select-all'}, tempPassword),
                        h('b-button', {props: {size: 'sm', variant: 'outline-secondary'}, on: {click: () => this.copyPassword(tempPassword)}}, '복사'),
                    ]),
                    h('p', {class: 'mb-0 small text-danger'}, '이 창을 닫으면 다시 볼 수 없습니다. 지금 복사해 사용자에게 전달하세요.'),
                ]),
                // 실수로 닫혀 비밀번호를 잃지 않도록 확인 버튼으로만 닫는다
                modalOptions: {noCloseOnBackdrop: true, noCloseOnEsc: true},
            });
        },
        async copyPassword(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.$EventBus.$emit('toast-success', '복사 완료', '초기 비밀번호를 복사했습니다.');
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '복사 실패', '복사하지 못했습니다. 비밀번호를 직접 선택해 복사해주세요.');
            }
        },
        resetModal() {
            this.loading = false;
            this.form = {
                e_mail: '',
                user_name: '',
                password: '',
                is_admin: 1,
            };
        },
    },
};
</script>
